import { ToastActions, ActivityState, OrganizationState, ActivityActions } from '@app/_store';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, insertItem, patch, updateItem, removeItem, compose } from '@ngxs/store/operators';
import { Observable, of } from 'rxjs';
import { RegistrationActions } from './registration.actions';
import { Registration, RegistrationStateModel, ProductPackage, ProductPackageProduct } from './registration.models';
import { map, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ActivityService } from '@app/_services';

const defaults = { saved: true, registration: undefined };

@State<RegistrationStateModel>({
  name: 'registration',
  defaults: defaults,
})
@Injectable()
export class RegistrationState {
  constructor(
    private store: Store,
    private activityService: ActivityService
  ) {}

  @Selector()
  static saved(state: RegistrationStateModel): boolean {
    return state.saved;
  }

  @Selector()
  static registration(state: RegistrationStateModel): Registration {
    return state.registration;
  }

  @Action(RegistrationActions.Initialize)
  initialize(ctx: StateContext<RegistrationStateModel>): Observable<void> {
    const activity = this.store.selectSnapshot(ActivityState.currentActivity);
    if (activity.registration) {
      const productPackages: ProductPackage[] = [];

      activity.registration.productPackages?.forEach(pp => {
        const packageCopy = {} as ProductPackage;
        Object.assign(packageCopy, pp);

        packageCopy.products = pp.products.map(ppp => {
          const product = activity.products.find(p => p.id == ppp.id);
          const productCopy = {} as ProductPackageProduct;
          Object.assign(productCopy, product);
          productCopy.isEditable = ppp.isEditable ?? product.isUserSelectable;
          return productCopy;
        });

        productPackages.push(packageCopy);
      });

      ctx.setState(
        patch<RegistrationStateModel>({
          saved: true,
          registration: {
            ...activity.registration,
            url: environment.registrationUrl
              .replace('{organizationId}', this.store.selectSnapshot(OrganizationState.current).id)
              .replace('{activityId}', activity.id),
            productPackages: productPackages,
          },
        })
      );
    } else {
      ctx.setState(
        patch<RegistrationStateModel>({
          saved: true,
          registration: undefined,
        })
      );
    }

    return of(null);
  }

  @Action(RegistrationActions.Reset)
  reset(ctx: StateContext<RegistrationStateModel>): Observable<void> {
    ctx.setState(defaults);
    return of(undefined);
  }

  @Action(RegistrationActions.Enable)
  enable(ctx: StateContext<RegistrationStateModel>): Observable<void> {
    const activity = this.store.selectSnapshot(ActivityState.currentActivity);
    const organization = this.store.selectSnapshot(OrganizationState.current);

    ctx.setState(
      patch<RegistrationStateModel>({
        saved: false,
        registration: {
          url: environment.registrationUrl.replace('{organizationId}', organization.id).replace('{activityId}', activity.id),
          formControls: [
            {
              id: 'name',
              title: 'Namn',
              description: undefined,
              type: 'text',
              required: true,
              locked: true,
              important: false,
            },
            {
              id: 'email',
              title: 'Epost',
              description: undefined,
              type: 'email',
              required: true,
              locked: true,
              important: false,
            },
            {
              id: 'phone',
              title: 'Telefonnummer',
              description: undefined,
              type: 'phone',
              required: true,
              locked: true,
              important: false,
            },
          ],
        },
      })
    );
    return of();
  }

  @Action(RegistrationActions.AddFormControl)
  addFormControl(ctx: StateContext<RegistrationStateModel>, action: RegistrationActions.AddFormControl): Observable<void> {
    ctx.setState(
      patch<RegistrationStateModel>({
        saved: false,
        registration: patch<Registration>({
          formControls: append([action.formControl]),
        }),
      })
    );
    return of();
  }

  @Action(RegistrationActions.ReorderFormControls)
  reorderFormControl(
    ctx: StateContext<RegistrationStateModel>,
    action: RegistrationActions.ReorderFormControls
  ): Observable<void> {
    if (action.previousIndex == action.currentIndex) return of();

    const formControls = ctx.getState().registration.formControls;
    const item = formControls[action.previousIndex];
    ctx.setState(
      patch<RegistrationStateModel>({
        saved: false,
        registration: patch<Registration>({
          formControls: compose(removeItem(action.previousIndex), insertItem(item, action.currentIndex)),
        }),
      })
    );
    return of();
  }

  @Action(RegistrationActions.RemoveFormControl)
  removeFormControl(ctx: StateContext<RegistrationStateModel>, action: RegistrationActions.RemoveFormControl): Observable<void> {
    ctx.setState(
      patch<RegistrationStateModel>({
        saved: false,
        registration: patch<Registration>({
          formControls: removeItem(x => x.id == action.id),
        }),
      })
    );
    return of();
  }

  @Action(RegistrationActions.UpdateConfirmationText)
  updateConfirmationText(
    ctx: StateContext<RegistrationStateModel>,
    action: RegistrationActions.UpdateConfirmationText
  ): Observable<void> {
    ctx.setState(
      patch<RegistrationStateModel>({
        saved: false,
        registration: patch<Registration>({
          confirmationText: action.confirmationText,
        }),
      })
    );
    return of();
  }

  @Action(RegistrationActions.AddProductPackage)
  addProductPackage(ctx: StateContext<RegistrationStateModel>, action: RegistrationActions.AddProductPackage): Observable<void> {
    ctx.setState(
      patch<RegistrationStateModel>({
        saved: false,
        registration: patch<Registration>({
          productPackages: append<ProductPackage>([action.pkg]),
        }),
      })
    );
    return of();
  }

  @Action(RegistrationActions.UpdateProductPackage)
  updateProductPackage(
    ctx: StateContext<RegistrationStateModel>,
    action: RegistrationActions.UpdateProductPackage
  ): Observable<void> {
    ctx.setState(
      patch<RegistrationStateModel>({
        saved: false,
        registration: patch<Registration>({
          productPackages: updateItem(p => p.id == action.pkg.id, action.pkg),
        }),
      })
    );
    return of();
  }

  @Action(RegistrationActions.DeleteProductPackage)
  deleteProductPackage(
    ctx: StateContext<RegistrationStateModel>,
    action: RegistrationActions.DeleteProductPackage
  ): Observable<void> {
    ctx.setState(
      patch<RegistrationStateModel>({
        saved: false,
        registration: patch<Registration>({
          productPackages: removeItem(x => x.id == action.id),
        }),
      })
    );
    return of();
  }

  @Action(RegistrationActions.Save)
  save(ctx: StateContext<RegistrationStateModel>): Observable<void> {
    return this.activityService
      .patchActivity([
        {
          op: 'replace',
          path: 'registration',
          value: ctx.getState().registration,
        },
      ])
      .pipe(
        map(() => {
          ctx.setState(
            patch<RegistrationStateModel>({
              saved: true,
            })
          );
        }),
        tap(() => {
          const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);
          if (!currentActivity.registration) return ctx.dispatch(new ActivityActions.View(currentActivity.id));
          return of(null);
        }),
        tap(() => {
          return this.store.dispatch(new ToastActions.ShowSuccess('Formuläret har sparats!'));
        })
      );
  }
}
